import React, { useState, useEffect, useMemo } from "react";
import {
  AppBar,
  Box,
  Toolbar,
  IconButton,
  Typography,
  Container,
  Grid,
  List,
  ListItem,
  Drawer,
  ListItemIcon,
  ListItemText,
  Button,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import PersonIcon from "@mui/icons-material/Person";
import LogoutIcon from "@mui/icons-material/Logout";
import HelpIcon from "@mui/icons-material/Help";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import { abcdLogoURL, abcdURLSegment, app_title, dhaneshLogoURL, dhaneshURLSegment, digiborrowLogoURL, digiborrowURLSegment, parkPlusLogoURL, parkPlusURLSegment, s3StaticImagePath, urlSegmentRegex, lakshyaSkillFinanceLogoURL, lakshyaSkillFinanceURLSegment } from "../config";
import { useTranslation } from "react-i18next";
import logoutApi from "../hooks/POST/logout";
import { defaultUrlSegment } from "../config";
import { useSelector, useDispatch } from "react-redux";
import { updateEditUserDetail } from "../utils/reduxSlice/tempSlice";
import { useNavigate } from "react-router-dom";
import NeedHelpDialog from "./NeedHelpDialog";
import { removeCookieAsync, saveCookieAsync } from "../utils";
import { setProductId, setProductType, setUrlSegment } from "../utils/reduxSlice/tokenSlice";

export default function UserHeader() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const logoURL = useSelector((state) => state.appInfo.logo);
  const urlSegment = useSelector((state) => state.token.urlSegment);
  const coBrandLogoURL = useSelector((state) => state.appInfo.coBrandLogoURL);
  const utms = useSelector((state) => state.token.utms);
  const lastLoginDate = localStorage.getItem("lastLoginDate");

  // whether to show powered by or not start
  const [showPoweredBy /* , setShowPoweredBy */] = useState(false);

  /* useEffect(() => {
    const intervalFunction = setInterval(() => {
    }, 1000);
    return () => clearInterval(intervalFunction);
  }, []); */
  // whether to show powered by or not end

  // user detail logic start
  const user = useSelector((state) => state.user);

  const [showUser, setShowUser] = useState(false);
  const pathName = window.location.pathname;

  const isABCDURL = urlSegment === abcdURLSegment || utms?.utm_id === abcdURLSegment;
  const isDhaneshURL = urlSegment === dhaneshURLSegment || utms?.utm_id === dhaneshURLSegment;
  const isDigiborrowURL = urlSegment === digiborrowURLSegment || utms?.utm_id === digiborrowURLSegment;
  const isParkPlusURL = urlSegment === parkPlusURLSegment || utms?.utm_id === parkPlusURLSegment;
  const isLakshyaSkillFinanceURL = urlSegment === lakshyaSkillFinanceURLSegment || utms?.utm_id === lakshyaSkillFinanceURLSegment;

  const getAppLogo = useMemo(() => {
    if(isABCDURL) {
      return abcdLogoURL;
    }
    if(isDhaneshURL) {
      return dhaneshLogoURL;
    }
    if(isDigiborrowURL) {
      return digiborrowLogoURL;
    }
    if(isLakshyaSkillFinanceURL) {
      return lakshyaSkillFinanceLogoURL;
    }
    return logoURL;
  }, [isABCDURL, logoURL, isDhaneshURL, isDigiborrowURL, isLakshyaSkillFinanceURL]);

  useEffect(() => {
    // const excludedPaths = ["/user/user-detail", "/user/welcome-back"];
    const excludedPaths = ["/user/user-detail", "/user/welcome-back", "/user/business-detail", "/user/business-welcome-back", "/user/business-offers"];
    const isPresent = excludedPaths.includes(pathName);
    setShowUser(isPresent ? false : true);
  }, [pathName]);
  // user detail logic end

  // whether to show return to profile or not start
  const [showReturnToProfile, setReturnToProfile] = useState(false);

  useEffect(() => {
    const allowedPaths = ["/user/offers"];
    const isPresent = allowedPaths.includes(pathName);
    setReturnToProfile(isPresent);
  }, [pathName]);
  // whether to show return to profile or not end

  // whether to show drawer or not start
  const [showDrawer, setShowDrawer] = useState(false);

  useEffect(() => {
    handleDrawerClose();
    const excludedPaths = ["/user/user-detail"];
    const isPresent = excludedPaths.includes(pathName);
    setShowDrawer(isPresent ? false : true);
  }, [pathName]);
  // whether to show drawer or not end

  //handle return to profile
  const handleReturnToProfile = (e) => {
    e.preventDefault();
    dispatch(updateEditUserDetail(true)); // enabling edit user detail flag in redux store
    navigate("/user/user-detail"); // navigating to user detail
  };

  // handle logout
  const handleLogout = async () => {
    await logoutApi();
  };

  // need help dialog start
  const [showHelpDialog, setShowHelpDialog] = useState(false);

  const handleOpenHelpDialog = () => {
    setShowHelpDialog(true);
  };

  const handleCloseHelpDialog = () => {
    setShowHelpDialog(false);
  };
  // need help dialog end

  // drawer start
  const [drawerOpen, setDrawerOpen] = useState(false);

  const handleDrawerOpen = () => {
    setDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
  };

  // handle return to product
  const handleReturnToProducts = async () => {
    if (urlSegment && urlSegment !== defaultUrlSegment) {
      await saveCookieAsync("urlSegment", urlSegment, 0);
      navigate(`/${urlSegment}`);
      return;
    }
    await removeCookieAsync("productType")
    dispatch(setProductType(null))
    await removeCookieAsync("productId")
    dispatch(setProductId(null))
    navigate("/products"); // navigating to products
  };

  const drawerBody = (
    <div role="presentation" style={{ width: 250 }}>
      <List>
        <ListItem style={{ justifyContent: "flex-end" }}>
          <IconButton onClick={handleDrawerClose} size="small">
            <CloseIcon />
          </IconButton>
        </ListItem>
        {lastLoginDate && <ListItem sx={{
          backgroundColor: "rgba(0, 0, 0, 0.04)",
          borderRadius: 1,
          mx: 1,
          mb: 1
        }}>
          <ListItemText
            primary="Last Login"
            secondary={localStorage.getItem("lastLoginDate") || "N/A"}
            primaryTypographyProps={{
              variant: "body2",
              fontWeight: 500,
              color: "text.secondary"
            }}
            secondaryTypographyProps={{
              variant: "body2",
              fontWeight: 400,
              color: "text.primary"
            }}
          />
        </ListItem>}
        {showUser && (
          <ListItem sx={{ color: "secondary.main" }}>
            <ListItemIcon sx={{ color: "secondary.main" }}>
              <PersonIcon />
            </ListItemIcon>
            <ListItemText primary={`${user.firstName} ${user.lastName}`} />
          </ListItem>
        )}
        {showReturnToProfile && (
          <ListItem
            button
            sx={{ color: "secondary.main" }}
            onClick={handleReturnToProfile}
          >
            <ListItemIcon sx={{ color: "secondary.main" }}>
              <ArrowBackIosIcon sx={{ paddingLeft: 0.5 }} />
            </ListItemIcon>
            <ListItemText primary={t("Return to Profile")} />
          </ListItem>
        )}
        {showReturnToProfile && !["loans", "goldloans"].includes(process.env.REACT_APP_SUBDOMAIN) && (
          <ListItem
            button
            sx={{ color: "secondary.main" }}
            onClick={handleReturnToProducts}
          >
            <ListItemIcon sx={{ color: "secondary.main" }}>
              <ArrowBackIosIcon sx={{ paddingLeft: 0.5 }} />
            </ListItemIcon>
            <ListItemText primary={t("Return to Products")} />
          </ListItem>
        )}
        <ListItem
          button
          sx={{ color: "secondary.main" }}
          onClick={handleOpenHelpDialog}
        >
          <ListItemIcon sx={{ color: "secondary.main" }}>
            <HelpIcon />
          </ListItemIcon>
          <ListItemText primary={t("Help")} />
        </ListItem>
        <ListItem
          button
          onClick={handleLogout}
          sx={{ color: "error.main", position: "fixed", bottom: 0 }}
        >
          <ListItemIcon sx={{ color: "error.main" }}>
            <LogoutIcon />
          </ListItemIcon>
          <ListItemText primary={t("Logout")} />
        </ListItem>
      </List>
    </div>
  );
  // drawer start

  return (
    <AppBar position="sticky" color="inherit" style={{ overflow: "auto" }}>
      <Container maxWidth="xl">
        <Toolbar disableGutters>
          {/* Desktop part start */}
          <Box sx={{ mr: 2, display: { xs: "none", md: "flex" } }}>
            {!coBrandLogoURL && getAppLogo && !isParkPlusURL && <img
              onClick={handleReturnToProducts}
              src={getAppLogo}
              alt={app_title}
              style={{ maxHeight: 75, maxWidth: 202, padding: 5, marginRight: 10, cursor: "pointer" }}
            />}
            {coBrandLogoURL && <img
              onClick={handleReturnToProducts}
              src={coBrandLogoURL}
              alt="partner"
              style={{ maxHeight: 55, maxWidth: 130, padding: 5, marginRight: 15, objectFit: "contain", cursor: "pointer" }}
            />}
            {(coBrandLogoURL || isABCDURL || isDhaneshURL || isDigiborrowURL || isLakshyaSkillFinanceURL) && (
              <Grid container>
                <Grid container alignItems="center">
                  <Typography variant="caption" style={{ marginTop: 0 }}>In Partnership With</Typography>
                </Grid>
                <Grid container>
                  <img
                    onClick={handleReturnToProducts}
                    src={logoURL}
                    alt={app_title}
                    style={{ maxWidth: 160, padding: "0 5px 5px 0", marginRight: 10, objectFit: "contain", cursor: "pointer" }}
                  />
                </Grid>
              </Grid>
            )}

            {/* park plus logo start */}
            {isParkPlusURL && <img
              src={logoURL}
              alt={app_title}
              style={{ maxWidth: 180, marginRight: 10, objectFit: "contain" }}
            />}
            {isParkPlusURL && (
              <Grid container>
                <Grid container alignItems="center">
                  <Typography variant="caption" style={{ marginTop: 0 }}>In Partnership With</Typography>
                </Grid>
                <Grid container>
                  <img
                    src={parkPlusLogoURL}
                    alt={app_title}
                    style={{ maxHeight: 40, maxWidth: 202, padding: 5, marginRight: 10 }}
                  />
                </Grid>
              </Grid>
            )}
            {/* park plus logo end */}

            {showPoweredBy && (
              <Grid container>
                <Grid container alignItems="center">
                  <Typography variant="caption">{t("Powered by")}</Typography>
                  <img
                    src={`${s3StaticImagePath}/ongo_logo.png`}
                    alt="powered by"
                    style={{ height: 15, marginLeft: 5 }}
                  />
                </Grid>
                <Grid container>
                  <Typography fontStyle="italic" variant="caption">
                    Dynamic Caption
                  </Typography>
                </Grid>
              </Grid>
            )}
          </Box>

          <Box sx={{ flexGrow: 1, display: { xs: "none", md: "flex" } }} />

          <Box sx={{ flexGrow: 0, display: { xs: "none", md: "flex" }, alignItems: "center", gap: 2 }}>
            {lastLoginDate && <Box
              sx={{
                display: "flex",
                alignItems: "center",
                backgroundColor: "rgba(0, 0, 0, 0.04)",
                borderRadius: 1,
                px: 2,
                py: 1,
                minWidth: "fit-content"
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                  fontWeight: 500,
                  whiteSpace: "nowrap",
                  fontSize: { md: "0.875rem", lg: "0.9rem" }
                }}
              >
                Last Login: {localStorage.getItem("lastLoginDate") || "N/A"}
              </Typography>
            </Box>}
            {showUser && (
              <Button
                startIcon={<PersonIcon />}
                sx={{ alignItems: "start" }}
                color="inherit"
                disableRipple
              >
                {user.firstName} {user.lastName}
              </Button>
            )}
            <Button
              startIcon={<HelpIcon />}
              sx={{ alignItems: "start" }}
              onClick={handleOpenHelpDialog}
              color="primary"
            >
              {t("Help")}
            </Button>
            <Button
              startIcon={<LogoutIcon />}
              color="error"
              sx={{ alignItems: "start" }}
              onClick={handleLogout}
            >
              {t("Logout")}
            </Button>
          </Box>
          {/* Desktop part end */}

          {/* Mobile part start */}
          <Box sx={{ flexGrow: 1, display: { xs: "flex", md: "none" } }}>
            {showDrawer && (
              <IconButton
                size="large"
                color="inherit"
                onClick={handleDrawerOpen}
              >
                <MenuIcon />
              </IconButton>
            )}
          </Box>
          <Drawer anchor="left" open={drawerOpen} onClose={handleDrawerClose}>
            {drawerBody}
          </Drawer>

          <Box sx={{ flexGrow: 1, display: { xs: "flex", md: "none" } }}>
            {!coBrandLogoURL && getAppLogo && !isParkPlusURL && <img
              onClick={handleReturnToProducts}
              src={getAppLogo}
              alt={app_title}
              style={{ maxHeight: 75, maxWidth: 202, padding: 5, marginRight: 10, cursor: "pointer" }}
            />}
            {coBrandLogoURL && <img
              onClick={handleReturnToProducts}
              src={coBrandLogoURL}
              alt="partner"
              style={{ maxHeight: 55, maxWidth: 110, padding: 5, marginRight: 10, objectFit: "contain", cursor: "pointer" }}
            />}
            {(coBrandLogoURL || isABCDURL || isDhaneshURL || isDigiborrowURL) && (
              <Grid container>
                <Grid container alignItems="center">
                  <Typography variant="caption" style={{ marginTop: 0 }}>In Partnership With</Typography>
                </Grid>
                <Grid container>
                  <img
                    onClick={handleReturnToProducts}
                    src={logoURL}
                    alt={app_title}
                    style={{ maxWidth: 140, padding: "0 5px 5px 0", marginRight: 0, objectFit: "contain", cursor: "pointer" }}
                  />
                </Grid>
              </Grid>
            )}

            {/* park plus logo start */}
            {isParkPlusURL && <img
              src={logoURL}
              alt={app_title}
              style={{ maxWidth: 180, marginRight: 10, objectFit: "contain" }}
            />}
            {isParkPlusURL && (
              <Grid container>
                <Grid container alignItems="center">
                  <Typography variant="caption" style={{ marginTop: 0 }}>In Partnership With</Typography>
                </Grid>
                <Grid container>
                  <img
                    src={parkPlusLogoURL}
                    alt={app_title}
                    style={{ maxHeight: 40, maxWidth: 202, padding: 5, marginRight: 10, objectFit: "contain" }}
                  />
                </Grid>
              </Grid>
            )}
            {/* park plus logo end */}
          </Box>

          <Box sx={{ flexGrow: 0, display: { xs: "flex", md: "none" } }}>
            {showPoweredBy && (
              <Grid container>
                <Grid container alignItems="center">
                  <Typography variant="caption">{t("Powered by")}</Typography>
                  <img
                    src={`${s3StaticImagePath}/ongo_logo.png`}
                    alt="powered by"
                    style={{ height: 15, marginLeft: 5 }}
                  />
                </Grid>
                <Grid container>
                  <Typography fontStyle="italic" variant="caption">
                    Dynamic Caption
                  </Typography>
                </Grid>
              </Grid>
            )}
          </Box>

          {/* Mobile part end */}

          {/* Need help dialog start */}
          <NeedHelpDialog
            open={showHelpDialog}
            handleClose={handleCloseHelpDialog}
          />
          {/* Need help dialog end */}
        </Toolbar>
      </Container>
    </AppBar>
  );
}
